<script setup lang="ts">
import type { CopilotConfigurationTemplate } from '@factoryfixinc/ats-interfaces';
import { computed } from 'vue';

const props = defineProps<{
  template: CopilotConfigurationTemplate;
  isSelected?: boolean;
  isDefault?: boolean;
  color?: string;
}>();

const emit = defineEmits<{
  (e: 'click'): void;
}>();

const borderColorClass = computed(() => {
  const colorMap = {
    purple: 'border-l-highlight-600',
    blue: 'border-l-inform-600',
    green: 'border-l-success-600',
    ruby: 'border-l-critical-600',
    primary: 'border-l-primary-600',
  };

  return colorMap[props.color as keyof typeof colorMap] || 'border-l-highlight-600';
});

const backgroundColorClass = computed(() => {
  const colorMap = {
    purple: 'bg-highlight-50/50',
    blue: 'bg-inform-50/50',
    green: 'bg-success-50/50',
    ruby: 'bg-critical-50/50',
    primary: 'bg-primary-50/50',
  };

  const hoverColorMap = {
    purple: 'hover:bg-highlight-50/50',
    blue: 'hover:bg-inform-50/50',
    green: 'hover:bg-success-50/50',
    ruby: 'hover:bg-critical-50/50',
    primary: 'hover:bg-primary-50/50',
  };

  if (props.isSelected) {
    return colorMap[props.color as keyof typeof colorMap] || 'bg-highlight-50/50';
  }

  return hoverColorMap[props.color as keyof typeof hoverColorMap] || 'hover:bg-highlight-50/50';
});

const handleClick = () => {
  emit('click');
};
</script>

<template>
  <div
    :class="[
      'flex w-full cursor-pointer items-start rounded-sm border-l-2 px-3 py-2 transition-colors duration-200',
      borderColorClass,
      backgroundColorClass,
    ]"
    @click="handleClick"
  >
    <div class="flex-1">
      <div class="flex items-center">
        <div class="text-base font-bold text-shade-900">{{ template.displayName }}</div>

        <span
          v-if="isDefault"
          class="ml-2 inline-block rounded-[40px] bg-primary-100 px-2 py-1 text-xs font-bold leading-[100%] text-primary-600"
        >
          FactoryFix Default
        </span>
      </div>

      <div class="mt-0.5 text-xs font-normal text-shade-820">{{ template.description }}</div>
    </div>
  </div>
</template>
